import { httpsCallable } from 'firebase/functions';
import { functionsInstance } from '../firebase';
import { captureFrontendException, addSentryBreadcrumb } from './sentryMonitoring';

export const GLOBAL_ISSUE_EVENT = 'gigtos:global-issue-alert';

const SAFE_USER_MESSAGE = 'Something went wrong. Our team is already checking it. Please try again in a moment.';

const EXPECTED_CLIENT_NOISE_PATTERNS = [
  /auth\/invalid-credential/i,
  /auth\/wrong-password/i,
  /auth\/user-not-found/i,
  /auth\/invalid-email/i,
  /auth\/email-already-in-use/i,
  /auth\/weak-password/i,
  /wrong password/i,
  /incorrect credentials/i,
  /invalid login/i,
  /phone.*not.*registered/i,
  /user denied geolocation/i,
  /geolocation.*permission.*denied/i,
  /geolocation.*timeout/i,
  /position unavailable/i,
  /failed to fetch/i,
  /network request failed/i,
  /err_internet_disconnected/i,
  /err_network_changed/i,
  /request aborted/i,
  /\baborterror\b/i,
];

const recentReports = new Map();

function stringifyError(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return [
    error.name,
    error.code,
    error.message,
    error.reason,
    error.status,
  ].filter(Boolean).join(' ');
}

function isExpectedClientNoise(error, context = {}) {
  const text = [
    stringifyError(error),
    context.source,
    context.action,
    context.message,
    context.code,
  ].filter(Boolean).join(' ');
  return EXPECTED_CLIENT_NOISE_PATTERNS.some(pattern => pattern.test(text));
}

function getRoute() {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname || ''}${window.location.hash || ''}`.slice(0, 300);
}

function makeFingerprint(error, context = {}) {
  const basis = [
    context.source || 'client_app',
    context.action || '',
    getRoute(),
    stringifyError(error).replace(/\d{3,}/g, '#').slice(0, 180),
  ].join('|');
  let hash = 0;
  for (let i = 0; i < basis.length; i += 1) {
    hash = ((hash << 5) - hash + basis.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

function shouldSendNow(fingerprint, windowMs = 5 * 60 * 1000) {
  const now = Date.now();
  const last = recentReports.get(fingerprint) || 0;
  if (now - last < windowMs) return false;
  recentReports.set(fingerprint, now);
  return true;
}

function showGlobalIssueAlert(payload = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GLOBAL_ISSUE_EVENT, {
    detail: {
      title: payload.title || 'We hit a small issue',
      message: payload.message || SAFE_USER_MESSAGE,
      severity: payload.severity || 'medium',
      reportId: payload.reportId || null,
      fingerprint: payload.fingerprint || null,
    },
  }));
}

function sanitizeContext(context = {}) {
  return {
    source: (context.source || 'client_app').toString().slice(0, 80),
    action: (context.action || '').toString().slice(0, 120),
    actorRole: (context.actorRole || '').toString().slice(0, 80),
    componentStack: (context.componentStack || '').toString().slice(0, 1800),
    code: (context.code || '').toString().slice(0, 80),
  };
}

export async function reportClientIssue(error, context = {}, options = {}) {
  const safeContext = sanitizeContext(context);
  if (!options.force && isExpectedClientNoise(error, safeContext)) {
    return { skipped: true, reason: 'expected_client_noise' };
  }

  const severity = options.severity || context.severity || 'medium';
  const fingerprint = context.fingerprint || makeFingerprint(error, safeContext);
  const errorText = stringifyError(error) || context.message || 'Client app issue';
  const stack = error?.stack || context.stack || '';

  if (options.showAlert !== false) {
    showGlobalIssueAlert({ severity, fingerprint });
  }

  addSentryBreadcrumb('Client issue reported', {
    source: safeContext.source,
    action: safeContext.action,
    route: getRoute(),
    fingerprint,
  });
  captureFrontendException(error instanceof Error ? error : new Error(errorText), {
    ...safeContext,
    fingerprint,
    route: getRoute(),
  });

  if (!shouldSendNow(fingerprint)) {
    return { skipped: true, reason: 'deduped', fingerprint };
  }

  try {
    const callable = httpsCallable(functionsInstance, 'reportClientIssue');
    const result = await callable({
      ...safeContext,
      severity,
      fingerprint,
      message: errorText,
      stack,
      route: getRoute(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      appVersion: process.env.REACT_APP_GIT_SHA || process.env.REACT_APP_VERSION || '',
      visibleToUser: options.showAlert !== false,
    });
    return result.data || { success: true, fingerprint };
  } catch (reportError) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Client issue backend report failed:', reportError);
    }
    return { skipped: true, reason: 'backend_report_failed', fingerprint };
  }
}

export function installGlobalClientIssueHandlers() {
  if (typeof window === 'undefined' || window.__gigtosClientIssueHandlersInstalled) return;
  window.__gigtosClientIssueHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportClientIssue(event.error || event.message, {
      source: 'window_error',
      message: event.message,
      action: 'global_error',
    }, { severity: 'high' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportClientIssue(event.reason || 'Unhandled promise rejection', {
      source: 'unhandled_rejection',
      action: 'global_promise_rejection',
    }, { severity: 'high' });
  });
}

export const getSafeClientIssueMessage = () => SAFE_USER_MESSAGE;
